import type { IComparisonFeatureRow, IComparisonSection } from '~/components/ComparisonTable.vue';

export enum TechnologyType {
    RealTimeUnified = 'real-time-unified',
    FirstGenUnified = 'first-gen-unified',
    IPaaS = 'ipaas',
    ETL = 'etl',
}

export const TECHNOLOGY_COLUMNS = [
    {
        name: 'ETL',
        subtitle: 'Extract, Transform, Load',
        key: TechnologyType.ETL,
        images: ['/logos/Boomi.png', '/logos/Fivetran.png'],
    },
    {
        name: 'iPaaS',
        subtitle: 'Integration Platform as a Service',
        key: TechnologyType.IPaaS,
        images: ['/logos/withampersand_logo.jpeg', '/logos/paragon.png'],
    },
    {
        name: 'Unified API',
        subtitle: 'First Generation',
        key: TechnologyType.FirstGenUnified,
        images: ['/logos/Merge.png', '/logos/Kombo.png', '/logos/apideck-white.png'],
    },
    {
        name: 'Unified.to',
        subtitle: 'Real-time Unified API',
        isHighlighted: true,
        key: TechnologyType.RealTimeUnified,
        images: ['/logos/Unified_White.png'],
    },
];

export const TECHNOLOGY_COMPARISON: IComparisonSection[] = [
    {
        title: '',
        rows: [
            {
                feature: 'Approach',
                text: {
                    [TechnologyType.RealTimeUnified]:
                        "Unified.to's Real-time Unified API combines unified API capabilities with real-time data access. By eliminating batch processing and storage, it ensures continuous, direct, and up-to-date data flow.",
                    [TechnologyType.FirstGenUnified]:
                        'This Unified API enables multiple integrations and standardizes data with one data model and API. However, its reliance on batch processing and storage results in delays when data is updated.',
                    [TechnologyType.IPaaS]:
                        "iPaaS was the first to enable integrations with SaaS platforms, but retains the batch-processing approach. While providing more flexibility than ETL, it's still limited by the lack of real-time data.",
                    [TechnologyType.ETL]:
                        'Designed 40+ years ago, ETL moves data by extracting it from a database, transforming it, and loading it into another. Data moves in batches at scheduled intervals and notably not in real-time.',
                },
            },
            {
                feature: 'Real-time Data',
                ratings: {
                    [TechnologyType.RealTimeUnified]: 5,
                    [TechnologyType.FirstGenUnified]: 2,
                    [TechnologyType.IPaaS]: 2,
                    [TechnologyType.ETL]: 2,
                },
                text: {
                    [TechnologyType.RealTimeUnified]: 'Data is delivered in real time and on demand, ensuring live availability.',
                    [TechnologyType.FirstGenUnified]: 'Batch processing may delay data updates by up to 24 hours.',
                    [TechnologyType.IPaaS]: 'Batch processing may delay data updates by up to 24 hours.',
                    [TechnologyType.ETL]: 'Batch processing may delay data updates by up to 24 hours.',
                },
            },
            {
                feature: 'Launch Speed',
                ratings: {
                    [TechnologyType.RealTimeUnified]: 5,
                    [TechnologyType.FirstGenUnified]: 3,
                    [TechnologyType.IPaaS]: 2,
                    [TechnologyType.ETL]: 1,
                },
                text: {
                    [TechnologyType.RealTimeUnified]:
                        "Minimal setup with no data model transformation required, as Unified.to's APIs deliver clean, normalized, real-time data, enabling high-volume launches in days.",
                    [TechnologyType.FirstGenUnified]:
                        'Shallow unified data schemas often require custom development, delaying launches as developers work to map and align data models effectively.',
                    [TechnologyType.IPaaS]: 'Each integration requires unique configuration for its API and data model, slowing high-volume launches.',
                    [TechnologyType.ETL]: 'ETL processes must be designed, tested, and scheduled for each source and destination, leading to slower launches.',
                },
            },
            {
                feature: 'Security',
                ratings: {
                    [TechnologyType.RealTimeUnified]: 5,
                    [TechnologyType.FirstGenUnified]: 3,
                    [TechnologyType.IPaaS]: 3,
                    [TechnologyType.ETL]: 3,
                },
                text: {
                    [TechnologyType.RealTimeUnified]: 'Unified.to ensures the security of transmitted data by never caching or storing it.',
                    [TechnologyType.FirstGenUnified]:
                        'Caching data during batch transfers increases security risks by temporarily exposing customer data outside the originating system.',
                    [TechnologyType.IPaaS]:
                        'Caching data during batch transfers increases security risks by temporarily exposing customer data outside the originating system.',
                    [TechnologyType.ETL]:
                        'Caching data during batch transfers increases security risks by temporarily exposing customer data outside the originating system.',
                },
            },
            {
                feature: 'Level of Unification',
                ratings: {
                    [TechnologyType.RealTimeUnified]: 5,
                    [TechnologyType.FirstGenUnified]: 3,
                    [TechnologyType.IPaaS]: 0,
                    [TechnologyType.ETL]: 0,
                },
                text: {
                    [TechnologyType.RealTimeUnified]:
                        'Unified.to is fully unified: API endpoints, data models, webhooks, permission scopes, and authorization.',
                    [TechnologyType.FirstGenUnified]: 'Partial unification due to shallow data models.',
                    [TechnologyType.IPaaS]: 'None',
                    [TechnologyType.ETL]: 'None',
                },
            },
            {
                feature: 'Maintenance',
                ratings: {
                    [TechnologyType.RealTimeUnified]: 5,
                    [TechnologyType.FirstGenUnified]: 3,
                    [TechnologyType.IPaaS]: 2,
                    [TechnologyType.ETL]: 1,
                },
                text: {
                    [TechnologyType.RealTimeUnified]:
                        'Zero maintenance burden as Unified.to fully manages API changes and updates. Plus full unified data objects.',
                    [TechnologyType.FirstGenUnified]: 'Partial unification increases technical debt for engineers.',
                    [TechnologyType.IPaaS]: 'iPaaS platforms manage most backend maintenance but still require oversight.',
                    [TechnologyType.ETL]: 'ETL processes need ongoing monitoring and adjustments to maintain pipeline integrity.',
                },
            },
            {
                feature: 'AI Usefulness',
                ratings: {
                    [TechnologyType.RealTimeUnified]: 5,
                    [TechnologyType.FirstGenUnified]: 3,
                    [TechnologyType.IPaaS]: 2,
                    [TechnologyType.ETL]: 2,
                },
                text: {
                    [TechnologyType.RealTimeUnified]: 'Unified.to delivers clean, normalized, and real-time data for training and fine-tuning AI models.',
                    [TechnologyType.FirstGenUnified]: 'Non-real-time and partially unified data impede efficient AI model training.',
                    [TechnologyType.IPaaS]: 'iPaaS is inefficient for training or fine-tuning AI due to the absence of clean, normalized, real-time data.',
                    [TechnologyType.ETL]: 'ETL is inefficient for training or fine-tuning AI due to the absence of clean, normalized, real-time data.',
                },
            },
            {
                feature: 'Authorization',
                ratings: {
                    [TechnologyType.RealTimeUnified]: 5,
                    [TechnologyType.FirstGenUnified]: 4,
                    [TechnologyType.IPaaS]: 1,
                    [TechnologyType.ETL]: 1,
                },
                text: {
                    [TechnologyType.RealTimeUnified]:
                        'Standardized auth across providers with normalized permission categories/scopes, tenant-scoped connections, automatic token refresh/rotation, secure secret storage, and auditable connection events.',
                    [TechnologyType.FirstGenUnified]:
                        'Integration-specific OAuth/API keys; token refresh and scope mapping typically left to your team or SDKs; higher risk of over-scoped access.',
                    [TechnologyType.IPaaS]:
                        'Per-connector credential vaults; OAuth flows and scopes vary by vendor are are all custom and external. Admin setup can be heavy; multi-tenant isolation depends on the platform.',
                    [TechnologyType.ETL]: 'Not applicable. Pipeline-level credentials only; no end-user authorization.',
                },
            },
            {
                feature: 'Pricing Model',
                ratings: {
                    [TechnologyType.RealTimeUnified]: 5,
                    [TechnologyType.FirstGenUnified]: 3,
                    [TechnologyType.IPaaS]: 2,
                    [TechnologyType.ETL]: 1,
                },
                text: {
                    [TechnologyType.RealTimeUnified]: 'Transparent: Usage-based pricing ensures predictable scaling and aligns with your pricing model.',
                    [TechnologyType.FirstGenUnified]: 'Tiered or contract-based pricing, but challenging to align costs with actual usage.',
                    [TechnologyType.IPaaS]: 'Flat-rate or task-based pricing, but scaling remains costly due to outdated architecture.',
                    [TechnologyType.ETL]:
                        'Pricing tied to hardware, software, or custom configurations, with high costs from manual maintenance of legacy systems.',
                },
            },
        ],
    },
] as const;
